// Messaging articles for the Help Center.

const messaging = {
  slug: 'messaging',
  label: 'Messaging',
  icon: 'PiChatText',
  description: 'How conversations, custom offers, and attachments work in your Inbox.',
  articles: [
    {
      slug: 'starting-a-conversation',
      title: 'Starting a conversation',
      summary: 'Message anyone directly from their profile or a listing.',
      body: [
        {
          type: 'p',
          text: 'You can message someone from their listing, their profile, or by searching for them from your Inbox. Every conversation you have, along with any orders, contracts, or agreements attached to it, lives in one place: your Inbox.',
        },
        {
          type: 'p',
          text: 'If a conversation is about a specific listing, that listing stays attached to the top of the chat. You can reference it, and if you are the seller, send a service agreement for it without leaving the conversation.',
        },
        {
          type: 'p',
          text: 'Some conversations include more than one other person, for example a group project or a few people coordinating on the same job. If someone leaves or is removed from a group conversation, a message is posted in the thread so everyone can see it happened.',
        },
      ],
    },
    {
      slug: 'sending-a-custom-offer',
      title: 'Sending a custom offer',
      summary: 'Propose a price and scope outside of a listing\'s standard packages.',
      body: [
        {
          type: 'p',
          text: 'Sometimes a listing\'s packages do not quite fit what someone needs. A custom offer lets a freelancer propose a specific price, description, delivery time, and number of revisions directly in a conversation, instead of using a fixed package.',
        },
        {
          type: 'p',
          text: 'The other person can accept, decline, or ask for changes. Once they accept, they can pay for it right from that same offer card, and the order proceeds like any other.',
        },
      ],
    },
    {
      slug: 'sharing-files-in-a-chat',
      title: 'Sharing files in a chat',
      summary: 'Attach images or documents directly to a message.',
      body: [
        {
          type: 'p',
          text: 'You can attach images and files to any message using the paperclip icon in the composer. This is useful for sharing reference material before an order starts, or for a seller delivering finished files once work is complete.',
        },
      ],
    },
    {
      slug: 'ask-unifreelancer',
      title: 'Ask UniFreelancer, our AI assistant',
      summary: 'A chat bubble that answers questions about the platform and looks up your own orders and balance.',
      body: [
        {
          type: 'p',
          text: 'Look for the chat bubble in the corner of the screen once you are signed in. Ask UniFreelancer answers general questions about how the site works, fees, escrow, and disputes, and can look up your own orders, balance, and tax status when you ask it to.',
        },
        {
          type: 'p',
          text: 'It only ever sees your own account. It cannot look up anyone else\'s information, take any action for you, like issuing a refund or cancelling an order, or see the actual text of your conversations with other people.',
        },
        {
          type: 'note',
          text: 'Nothing in that chat is saved. Refresh the page and it starts over.',
        },
      ],
    },
    {
      slug: 'notifications-explained',
      title: 'Notifications explained',
      summary: 'What triggers a notification, and how to manage them.',
      body: [
        {
          type: 'p',
          text: 'You get a notification for things that need your attention: a new message, someone accepting or declining a service agreement, a delivery you need to review, or a payment being released. Notifications appear in the bell icon at the top of the site and, depending on your settings, by email as well.',
        },
        {
          type: 'p',
          text: 'You can manage which notifications you receive from Settings, under Notifications.',
        },
      ],
    },
    {
      slug: 'spotting-a-scam',
      title: 'Spotting a scam',
      summary: 'The pitches we actually get here, what is really happening, and what to do about it.',
      body: [
        {
          type: 'p',
          text: 'We are the student dev team that built this site, and these have been sent to us too. The people running them look for new freelancers, because a first client feels like a chance you cannot afford to question. You can question it. Here is every pitch we have seen on UniFreelancer so far, and what is actually going on behind each one.',
        },
        {
          type: 'p',
          text: 'UniFreelancer will never ask you for any of the following, and no real client has a reason to either:',
        },
        {
          type: 'list',
          items: [
            'Your password, or a login or verification code. Not support, not an admin, not ever.',
            'Your social security number, a photo of your ID, your passport, or your bank account and routing numbers. Tax and identity details go to Stripe when you set up payouts, never to a person in a message.',
            'A fee to start working. No registration fee, training fee, equipment fee, or background check fee.',
            'Payment or work outside the platform, where there is no escrow, no refund and no record.',
          ],
        },
        {
          type: 'p',
          text: 'The middleman pitch: "be my middleman", "work under your account", "I do the work, you talk to the client". What is really happening is that they want your name, your account and your country on the paperwork, because they cannot get their own. The work, the taxes and the legal responsibility land on you, the money and the client stay with them, and your account gets banned for it. This one has reached two of us directly. It is the most common pitch here, and it is never legitimate.',
        },
        {
          type: 'p',
          text: 'Identity harvesting: "send your SSN and ID to onboard", "I need your bank details for the deposit", "text me the code you just got". This is identity theft with a job offer in front of it. A code you forward is your account, and a document you send cannot be taken back.',
        },
        {
          type: 'p',
          text: 'Advance fees: "there is a training fee", "buy the software first and I will reimburse you", "send a deposit and I will release the project". Money you send to a client is gone, and the project never arrives. Real clients pay you.',
        },
        {
          type: 'p',
          text: 'The overpayment check: "I will send a check for more than we agreed, refund me the difference". The check clears on paper, then reverses days later. The difference you sent back was your own money.',
        },
        {
          type: 'p',
          text: 'Going off-platform: "let us finish this on WhatsApp", "Venmo is easier", "we can skip the fees". Off the site there is no escrow holding the money, nothing to refund, no agreement to point at and nobody to help if it goes wrong. It is also against our Terms, which means we cannot protect you even if we want to.',
        },
        {
          type: 'p',
          text: 'Links and files: "click this link to get started", "join our Telegram group", an attachment that is not the kind of file the work needs. These exist to take your login or install something. Nothing about a real job on this site needs you to leave it.',
        },
        {
          type: 'note',
          text: 'If someone asks you to be their middleman, or to work under their account, that is the whole scam. There is no version of it that pays. Do not negotiate it, just report it.',
        },
        {
          type: 'p',
          text: 'What to do when you see one:',
        },
        {
          type: 'steps',
          items: [
            'Stop replying. You owe a stranger nothing, and silence costs you nothing.',
            'Send nothing: no files, no documents, no personal details, no money.',
            'Report the account from the conversation, using the menu at the top of the chat. Reports are private; they never learn who reported them.',
            'Block them if you would rather not hear from them again.',
            'If you already sent money or details, tell us anyway. Change your password, and call your bank if any bank or card details went out. Nobody here is going to judge you for it.',
          ],
        },
        {
          type: 'p',
          text: 'We also watch for these patterns automatically. When a message, job posting or proposal matches one, it is flagged for our team to review, and we suspend accounts that are running them. Your report is still the strongest signal we get, so send it even if you think we must already know.',
        },
      ],
    },
  ],
};

export default messaging;
